/**
 * User-schema checkbox list for the Database Blueprint setup dialog.
 * System schemas are excluded by default; the "Show system schemas" toggle
 * lives in the parent dialog.
 */

import { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

type Props = {
  availableSchemas: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  loading?: boolean;
};

export function SchemaCheckboxList({ availableSchemas, selected, onChange, loading }: Props) {
  const allSelected = useMemo(
    () =>
      availableSchemas.length > 0 &&
      availableSchemas.every((s) => selected.has(s)),
    [availableSchemas, selected]
  );

  const toggle = (schema: string) => {
    const next = new Set(selected);
    if (next.has(schema)) next.delete(schema);
    else next.add(schema);
    onChange(next);
  };

  const toggleAll = () => {
    if (allSelected) onChange(new Set());
    else onChange(new Set(availableSchemas));
  };

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="database-blueprint-schemas-loading">
        Loading schemas…
      </div>
    );
  }
  if (availableSchemas.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="database-blueprint-schemas-empty"
      >
        No user schemas were found in this database.
      </div>
    );
  }

  // Finding #22g — when there's only one schema available, clearing it
  // would silently block submit. Disable the toggle so the affordance is
  // visible but inert.
  const toggleDisabled = availableSchemas.length <= 1;

  return (
    <div className="space-y-2" data-testid="database-blueprint-schemas">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {selected.size} of {availableSchemas.length} selected
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleAll}
          disabled={toggleDisabled}
          data-testid="database-blueprint-toggle-all-schemas"
        >
          {allSelected ? 'Clear' : 'Select all'}
        </Button>
      </div>
      <ScrollArea className="h-32 rounded border bg-background p-2">
        <div className="grid grid-cols-2 gap-1">
          {availableSchemas.map((schema) => {
            const id = `blueprint-schema-${schema}`;
            return (
              <div key={schema} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={selected.has(schema)}
                  onCheckedChange={() => toggle(schema)}
                  data-testid={`blueprint-schema-${schema}`}
                />
                <Label htmlFor={id} className="text-sm">
                  {schema}
                </Label>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
